import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import type { LinkshimResponseAjax } from '@freelancer/datastore/collections';
import { LinkshimResultTypes } from '@freelancer/datastore/collections';
import { FreelancerHttp, HttpHeaders } from '@freelancer/freelancer-http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import type {
  LinkshimSuccessResponse,
  LinkshimErrorResponse,
} from './linkshim.interface';

/**
 * This service creates the HTTP GET request required to the AJAX API to check
 * if a URL is safe and provide an allow/block/warn action accordingly.
 *
 * Docs available here: https://phabricator.tools.flnltd.com/w/gaf/linkshim/
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class LinkshimService {
  constructor(
    private auth: Auth,
    private freelancerHttp: FreelancerHttp,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  checkLinkshim(
    url?: string | null,
    b64url?: string | null,
    sig?: string | null,
  ): Promise<LinkshimSuccessResponse | LinkshimErrorResponse> {
    let headers = new HttpHeaders();
    if (isPlatformBrowser(this.platformId)) {
      headers = headers.set('XSRF-TOKEN', this.auth.getCSRFToken());
    }

    const options = {
      isGaf: true,
      headers,
    };

    if (b64url) {
      return firstValueFrom(
        this.freelancerHttp
          .get<LinkshimResponseAjax>(
            `users/linkshim.php?b64url=${b64url}`,
            options,
          )
          .pipe(untilDestroyed(this)),
      );
    }

    if (url && sig) {
      return firstValueFrom(
        this.freelancerHttp
          .get<LinkshimResponseAjax>(
            `users/linkshim.php?url=${url}&sig=${sig}`,
            options,
          )
          .pipe(untilDestroyed(this)),
      );
    }

    if (url) {
      return firstValueFrom(
        this.freelancerHttp
          .get<LinkshimResponseAjax>(`users/linkshim.php?url=${url}`, options)
          .pipe(untilDestroyed(this)),
      );
    }

    // If in the case there is a missing URL or other error, show the
    // error using linkshim component.
    return Promise.resolve({
      status: 'error',
      result: { message: LinkshimResultTypes.ERROR },
    });
  }
}
